import React, { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from "lucide-react";

const Contact = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-obsidian pt-20 flex items-center justify-center px-4">
        <div className="text-center max-w-lg">
          <CheckCircle className="w-16 h-16 text-electric mx-auto mb-6" />
          <h1 className="font-display text-3xl text-white mb-4">
            Message Sent!
          </h1>
          <p className="text-white/60 mb-8">
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                first_name: "",
                last_name: "",
                email: "",
                subject: "",
                message: ""
              });
            }}
            variant="outline"
            className="border-electric text-electric hover:bg-electric/10"
          >
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian pt-20" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-electric font-display italic mb-2">Get in Touch</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white">
            Contact Us
          </h1>
          <p className="text-white/60 mt-4 max-w-xl">
            Have a question, comment, or just want to say hello? 
            We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-display text-2xl text-white mb-6">Send a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-white/70">First Name *</Label>
                    <Input 
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      className="bg-obsidian border-white/20 text-white focus:border-electric"
                      data-testid="contact-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-white/70">Last Name *</Label>
                    <Input 
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      className="bg-obsidian border-white/20 text-white focus:border-electric"
                      data-testid="contact-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white/70">Email *</Label>
                  <Input 
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-obsidian border-white/20 text-white focus:border-electric"
                    data-testid="contact-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-white/70">Subject *</Label>
                  <Input 
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="bg-obsidian border-white/20 text-white focus:border-electric"
                    data-testid="contact-subject"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white/70">Message *</Label>
                  <Textarea 
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    rows={6}
                    className="bg-obsidian border-white/20 text-white focus:border-electric resize-none"
                    placeholder="How can we help you?"
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="bg-electric text-obsidian hover:bg-electric-light px-8 py-6"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <span className="w-5 h-5 border-2 border-obsidian border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info & Map */}
            <div>
              <h2 className="font-display text-2xl text-white mb-6">Visit Us</h2>
              
              {/* Info Cards */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4 p-6 border border-white/10 rounded-lg bg-obsidian-light">
                  <MapPin className="w-6 h-6 text-electric flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Location</h3>
                    <p className="text-white/60">
                      2756 Hennepin Ave<br />
                      Minneapolis, MN 55408
                    </p>
                    <a 
                      href="https://maps.google.com/?q=2756+Hennepin+Ave+Minneapolis+MN"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-electric text-sm hover:underline mt-2 inline-block"
                    >
                      Get Directions →
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 border border-white/10 rounded-lg bg-obsidian-light">
                  <Mail className="w-6 h-6 text-electric flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Email</h3>
                    <p className="text-white/60">info@brownstonejazzclub.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 border border-white/10 rounded-lg bg-obsidian-light">
                  <Clock className="w-6 h-6 text-electric flex-shrink-0" />
                  <div>
                    <h3 className="text-white font-medium mb-1">Hours</h3>
                    <p className="text-white/60 text-sm">
                      Open during scheduled shows only
                    </p>
                    <p className="text-white/40 text-xs mt-1">
                      Check events for show times
                    </p>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="relative rounded-lg overflow-hidden">
                <img 
                  src="/assets/exterior.jpg"
                  alt="BrownStone Jazz Club exterior"
                  className="w-full aspect-video object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 to-transparent flex items-end p-6">
                  <p className="font-display text-white text-xl italic">
                    "Where the music breathes"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
